import React from 'react';

import Box from '@mui/material/Box';

import VETopBar from './VETopBar';
import VESettingsPanel from './VESettingsPanel';
import VEPreviewPanel from './VEPreviewPanel';
import VEMovieMenuPanel from './VEMovieMenuPanel';

import { useMovie } from "../movie/MovieProvider";
import { useMovieSetter } from "../movie/MovieProvider";

import { ThemeProvider, createTheme } from '@mui/material/styles';

import J2VAuth from "../libs/auth.js";
import APIHelper from "../libs/api.js";
import { useAlert } from './AlertContext';

const websiteBaseURL = 'https://web.json2video.com/data';
const tplBaseURL = 'https://cdn.json2video.com/data';
const googleFontsAPIKey = 'AIzaSyARuwCmNDlx8asaDRBq5EIk05fZprTwew0';

export default function VisualEditor(props) {

    const movie = useMovie();
    const setMovie = useMovieSetter();

    const { showAlert } = useAlert();

    const theme = createTheme({
        components: {
            MuiTextField: {
                defaultProps: {
                    variant: 'standard',
                },
            },
        }
    });

    let childProps = {};
    // [childProps.movieJSON, childProps.setMovieJSON] = React.useState(defaultMovie);
    [childProps.selectedSceneIndex, childProps.setSelectedSceneIndex] = React.useState(-1);
    [childProps.selectedElementIndex, childProps.setSelectedElementIndex] = React.useState(-1);
    [childProps.schema, childProps.setSchema] = React.useState(null);
    [childProps.componentsConfig, childProps.setComponentsConfig] = React.useState(null);
    [childProps.componentsSchema, childProps.setComponentsSchema] = React.useState(null);

    [childProps.userCredentials, childProps.setUserCredentials] = React.useState(J2VAuth.getCredentials());
    [childProps.loadedTemplate, childProps.setLoadedTemplate] = React.useState(null);
    [childProps.savingStatus, childProps.setSavingStatus] = React.useState("preview");
    childProps.loadTemplate = loadTemplate;
    childProps.saveTemplate = saveTemplate;

    childProps.screenWidth = document.documentElement.clientWidth || document.body.clientWidth;
    childProps.screenHeight = document.documentElement.clientHeight || document.body.clientHeight;
    childProps.APISchema = new window.OpenAPIHelper(`${websiteBaseURL}/api/json2video-api.json`);

    // Run this once in the beginning:
    React.useEffect(() => {
        childProps.setSchema(childProps.APISchema.apidoc);
        loadComponentsConfig();

        const urlParams = new URLSearchParams(window.location.search);
        const templateURL = urlParams.get('template');
        const exampleURL = urlParams.get('example');

        console.log("VisualEditor: ", templateURL, exampleURL);

        try {
            if (templateURL) {
                console.log("Loading template movie...", templateURL);
                loadTemplate(templateURL);
            }
            else if (exampleURL) {
                console.log("Loading example movie...", exampleURL);
                const url = (exampleURL.startsWith("http")) ? exampleURL : `https://json2video.com/data/examples${exampleURL}`;
                fetch(url).then(r => r.json()).then(exampleMovie => {
                    console.log("Example movie loaded", exampleMovie);
                    setMovie({
                        type: "updateMovie",
                        movie: exampleMovie
                    });
                }).catch(error => {
                    console.log("Error loading example movie: ", url);
                });
            }
            else {
                // if nothing to load, load default movie
                setMovie({
                    type: "updateMovie",
                    movie: defaultMovie
                });
            }
        }
        catch (error) {
            console.log("Loading movie error: ", error.toString());
            showAlert({
                title: "Error loading the template", 
                message: [
                    "Sorry, there was an error loading the template:",
                    "",
                    `<b>${error.toString()}</b>` 
                ] 
            });
            setMovie({
                type: "updateMovie",
                movie: defaultMovie
            });
        }
    }, []);

    // Run this every time movie changes
    React.useEffect(() => {
        // Run autosave no more than every 5 seconds
        let timeoutId;

        const debouncedHandleMovieObjectChange = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(saveTemplate, 5000); // Debounce time: 5 seconds
        };

        // Initial setup
        childProps.setSavingStatus("not_saved");

        // Debounced effect
        const cleanup = () => {
            clearTimeout(timeoutId);
        };

        debouncedHandleMovieObjectChange();

        return cleanup;
    }, [movie.movieObject, childProps.loadedTemplate]);

    return (
        <ThemeProvider theme={theme}>
            <Box className="fixed flex w-full h-full flex-col">
                <VETopBar {...childProps} defaultMovie={emptyMovie} />
                <Box key="scene-panels" className="h-0 flex flex-row flex-grow">
                    <VEMovieMenuPanel {...childProps} />
                    <VEPreviewPanel {...childProps} />
                    {childProps.schema && <VESettingsPanel {...childProps} />}
                </Box>
            </Box>
        </ThemeProvider>
    );

    async function loadComponentsConfig() {
        const url = `${tplBaseURL}/components/config.json`;
        console.log("Loading components config from: ", url);
        let config = await fetch(url).then(r => r.json()).catch(e => console.log("Error loading components config: ", url));

        // sort config["css-properties"] alphabetically
        config.cssPropertiesList = Object.keys(config["cssProperties"]).sort();

        let schema = {};

        // console.log("componentConfig: ", config);
        // console.log("componentSchema: ", schema);
        childProps.setComponentsConfig(config);
        childProps.setComponentsSchema(schema);
    }

    async function loadTemplate(templateID) {

        // load the template
        let errorMessage = "";
        const apiCall = await APIHelper.getTemplate(templateID);
        console.log("Load template response: ", apiCall);

        if (apiCall) {
            if (apiCall.success) {
                setMovie({
                    type: "updateMovie",
                    movie: JSON.parse(apiCall.template.movie)
                });
                childProps.setLoadedTemplate(apiCall.template);
                childProps.setSavingStatus("saved");

                if (!apiCall.template.owner) {
                    showAlert({
                        title: "Be aware!",
                        message: [
                            `This template "${apiCall.template.name}" is not owned by you and your changes will not be saved.`,
                            "&nbsp;",
                            `You can use <b>"Save template as..."</b> to save this template in your account.`
                        ]
                                    
                    });
                }
                return;
            }
            errorMessage = apiCall.message;
        }
        else {
            errorMessage = "API call failed";
        }

        showAlert({
            title: "Error loading template",
            message: [
                "Sorry, there was an error loading the template:",
                "",
                errorMessage
            ]
        });
    }

    async function saveTemplate() {

        console.log("saveTemplate(): ", childProps.loadedTemplate);

        if (!childProps.loadedTemplate) {
            childProps.setSavingStatus("preview");
            return;
        }
        if (!childProps.userCredentials) {
            childProps.setSavingStatus("preview");
            return;
        }
        if (childProps.loadedTemplate && !childProps.loadedTemplate.owner) {
            // loaded a template but not owned by the user
            childProps.setSavingStatus("preview");
            return;
        }

        // Show the saving icon
        childProps.setSavingStatus("saving");

        const newTemplate = {
            name: childProps.loadedTemplate.name,
            movie: JSON.stringify(movie.movieObject)
        };

        const apiCall = await APIHelper.saveTemplate(childProps.loadedTemplate.id, newTemplate);
        if (!apiCall || !apiCall.success) {
            showAlert({
                title: "Error saving template",
                message: [
                    "There was an error saving your template and changes were not saved:",
                    "",
                    apiCall.message ?? "Please try again later."
                ]
            });
            childProps.setSavingStatus("error");
            return;
        }

        // Show the saved icon
        childProps.setSavingStatus("saved");
    }
}

const defaultMovie = {
    "comment": "Default movie",
    "width": 1920,
    "height": 1080,
    "quality": "medium",
    "draft": false,
    "scenes": [
        {
            "comment": "Scene 1",
            "elements": [
                {
                    "type": "text",
                    "text": "Hello world",
                    "duration": 5
                }
            ]
        }
    ],
    "elements": [],
    "resolution": "custom",
    "fps": 25,
    "settings": {},
    "cache": true
};

const emptyMovie = defaultMovie;